import { Tab } from '@/utils/tabs';
import { DownloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery as useReactQuery } from '@tanstack/react-query';
import { App, Badge, Button, Form, Spin, Tabs, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthProvider';
import { setCompanies } from '../../../contextRedux/Company/CompanyStore';
import { setDevices } from '../../../contextRedux/DeviceType/DeviceTypeStore';
import { setLaborExtras } from '../../../contextRedux/LaborExtra/LaborExtraStore';
import {
  setPmContractProject,
  updateEquipmentTab,
  updateField,
} from '../../../contextRedux/PMContractProject/PmContractModelProject';
import { setPmContractProjectStatuses } from '../../../contextRedux/PmContractProjectStatus/PmContractProjectStatusStore';
import { setPmOffers } from '../../../contextRedux/PMOffer/PmOfferStore';
import { setPmOfferContractTypes } from '../../../contextRedux/PmOfferContractTypes/PmOfferContractTypesStore';
import { setSystemTypes } from '../../../contextRedux/SystemType/SystemTypeStore';
import { setUnitMeasures } from '../../../contextRedux/UnitMeasure/UnitMeasureStore';
import { setUsers } from '../../../contextRedux/User/UserStore';
import CompanyService from '../../../services/company/CompanyService';
import ConfigurationService from '../../../services/configuration/ConfigurationService';
import DeviceTypeService from '../../../services/deviceType/DeviceTypeService';
import LaborExtraService from '../../../services/laborExtra/LaborExtraService';
import PmContractService from '../../../services/pmContract/PmContractService';
import PmOfferService from '../../../services/pmOffer/PmOfferService';
import SystemTypeService from '../../../services/systemType/SystemTypeService';
import UserService from '../../../services/user/UserService';
import WorkPlaceService from '../../../services/workPlace/WorkPlaceService';
import { RootState } from '../../../store';
import './NewPmContractProject.css';
import AddEquipmentTab from './Tabs/AddEquipmentTab/AddEquipmentTab';
import CompanyDetailsTab from './Tabs/CompanyDetailsTab/CompanyDetailsTab';
import LaborTab from './Tabs/LaborTab/LaborTab';
import RaportTab from './Tabs/RaportTab/RaportTab';
import ReviewTab from './Tabs/ReviewTab/ReviewTab';
import SummaryTab from './Tabs/SummaryTab/SummaryTab';

const NewPmContractProject: React.FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 720 });
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const [form] = Form.useForm();

  const [id, setId] = useState<string | null>(query.get('id'));

  const { modal, message } = App.useApp();
  const { profile } = useAuth();

  const reduxPmContractProject = useSelector((state: RootState) => state?.models.pmContractProject);
  const addedDevices = useSelector((state: RootState) => state.models.pmContractProject.pmContractWorkPlaceItems);
  const companies = useSelector((state: RootState) => state.store.company.data);

  let totalOfferValue =
    Number.parseFloat(
      (
        reduxPmContractProject.pmContractWorkPlaceItems.reduce((total, item) => total + item.laborPrice, 0) +
        (reduxPmContractProject.pmContractLaborExtras
          ? reduxPmContractProject.pmContractLaborExtras.reduce(
              (total: any, item: any) => total + item?.piece * item?.price,
              0
            )
          : 0)
      ).toFixed(2)
    ) +
    Number.parseFloat(
      reduxPmContractProject.pmContractWorkPlaceItems
        .reduce((total, item) => {
          const totalPrice = item.price;
          return total + totalPrice;
        }, 0)
        .toFixed(2)
    );

  const [canGeneratePVRLT, setCanGeneratePVRT] = useState<boolean>(false);
  const [canSavePmContract, setCanSavePmContract] = useState<boolean>(false);

  const userRols = 'sales, tehnician, senior_tehnician, dispatcher';

  const isShowCompanyDetailsTab: boolean = !!(
    reduxPmContractProject.companyId &&
    reduxPmContractProject.accountManagerId &&
    (reduxPmContractProject.accountSaleId || reduxPmContractProject.projectAuthorId) &&
    reduxPmContractProject.enumPmOfferContractTypeId &&
    reduxPmContractProject.enumPmContractProjectStatusId &&
    reduxPmContractProject.date &&
    reduxPmContractProject.dateStart &&
    reduxPmContractProject.dateEnd &&
    reduxPmContractProject.registrationNumber
  );

  const { data: _users, isFetching: isUsersLoading } = useReactQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: userRols,
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const idFromUrl = query.get('id');
    if (!idFromUrl) {
    }
    setId(idFromUrl);
  }, [location.search]);

  const { data: _pmContract, isFetching: isPmContractLoading } = useReactQuery({
    queryKey: ['pmContracts'],
    queryFn: () =>
      PmContractService.search({
        ids: [id!],
        isActive: true,
        isStatusIncluded: true,
        isTypeIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }).then((data) => {
        dispatch(setPmContractProject(data?.response[0]));
        return data?.response[0];
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _offer, isFetching: isPmOffersLoading } = useReactQuery({
    queryKey: ['pmOffers'],
    queryFn: () =>
      PmOfferService.search({
        companyId: reduxPmContractProject.companyId,
        isActive: true,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isTypeIncluded: true,
        isPmOfferWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
        isLaborExtraIncluded: true,
        isFilesIncluded: true,
      }).then((data) => {
        dispatch(setPmOffers(data?.response));
        return data?.response;
      }),
    enabled:
      !!reduxPmContractProject.companyId &&
      (reduxPmContractProject.id == '' || reduxPmContractProject.id == undefined || reduxPmContractProject.id == null),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _status, isFetching: isStatusLoading } = useReactQuery({
    queryKey: ['pmContractProjectStatuses'],
    queryFn: () =>
      ConfigurationService.searchPmContractProjectStatus({}).then((data) => {
        dispatch(setPmContractProjectStatuses(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _contractTypes, isFetching: isPmOfferTypesLoading } = useReactQuery({
    queryKey: ['pmOfferContractTypes'],
    queryFn: () =>
      ConfigurationService.searchPmOfferContractType({}).then((data) => {
        dispatch(setPmOfferContractTypes(data?.response?.filter((pc: any) => [2].includes(pc.id))));
        return data?.response?.filter((pc: any) => [2].includes(pc.id));
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _companies, isFetching: isCompaniesLoading } = useReactQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _unitMeasures, isFetching: isUnitMeasuresLoading } = useReactQuery({
    queryKey: ['unitMeasures'],
    queryFn: () =>
      ConfigurationService.searchConfiguration({ tag: 'unitMeasure' }).then((data) => {
        dispatch(setUnitMeasures(data?.response?.section));

        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _laborExtras, isFetching: isLaborExtrasLoading } = useReactQuery({
    queryKey: ['laborExtras'],
    queryFn: () =>
      LaborExtraService.search({ isActive: true }).then((data) => {
        dispatch(setLaborExtras(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _systemTypes, isFetching: isSystemTypesLoading } = useReactQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setSystemTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _deviceTypes, isFetching: isDeviceTypesLoading } = useReactQuery({
    queryKey: ['deviceTypes'],
    queryFn: () =>
      DeviceTypeService.search({
        isSystemTypeIncluded: true,
        isLaborIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setDevices(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _pmContractComments,
    refetch: searchComments,
    isFetching: isPmContractCommentsLoading,
  } = useReactQuery({
    queryKey: ['pmContractComment'],
    queryFn: () =>
      PmContractService.searchComment({
        ids: [id!],
      }).then((data) => {
        const departments = ['general', 'contabilitate', 'manager', 'instalatori'];

        // Initialize offerComments with all departments
        const initialContractComments = departments.map((departmentName) => {
          const existingComment = data?.response?.find((comment: any) => comment.departmentName === departmentName);
          return (
            existingComment || {
              id: '',
              pmContractId: id,
              departmentName,
              message: '',
              pmContractCommentFiles: [],
            }
          );
        });
        dispatch(updateField({ field: 'pmContractCommentsTab', value: initialContractComments }));
        return data?.response;
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  // const { data: _interventionFileProjects, isPending: isLoadingInterventionFileProject } = useReactQuery({
  //   queryKey: ['interventionFileProject'],
  //   queryFn: () =>
  //     InterventionFilePmService.search({
  //       pmContractId: id,
  //       isWorkPlaceIncluded: true,
  //       isCompanyIncluded: true,
  //     }).then((data) => {
  //       dispatch(updateField({ field: 'interventionFileProjects', value: data?.response }));
  //       return data?.response[0];
  //     }),
  //   enabled: !!id,
  //   refetchOnWindowFocus: false,
  //   staleTime: 0,
  //   refetchOnMount: true,
  //   refetchOnReconnect: true,
  // });

  const { mutate: saveContract, isPending: isSavingContract } = useMutation({
    mutationFn: (formData: any) => PmContractService.upsert(id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data);
        data?.response && navigate(`/project-management/pmContractProject/edit?id=${data?.response?.id}`);
        if (!!id) {
          searchComments();
          if (data?.response?.enumPmContractProjectStatusId == 3) {
            setCanGeneratePVRT(true);
          } else setCanGeneratePVRT(false);
        }
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  const onSaveContract = () => {
    saveContract({
      ...reduxPmContractProject,
      pmContractComments: reduxPmContractProject.pmContractCommentsTab,
      pmContractSectionFiles: reduxPmContractProject.pmContractSectionFiles,
      pmContractIntervals: form?.getFieldValue('pmContractIntervals'),
    });

    setCanSavePmContract(
      addedDevices?.some((item: any) => item.addition < 50)! && !!reduxPmContractProject.enumPmContractProjectStatusId
    );
  };

  const { mutate: updateWorkPace } = useMutation({
    mutationFn: (formData: any) => WorkPlaceService.upsert(formData.id, formData),
    onSuccess: () => {},
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const filterAndGroupData = (data: any) => {
    const result: any = {};

    data.forEach((item: any) => {
      const { workPlace, workPlaceId, systemTypeId, deviceTypeId, piece, isAnotherProvider } = item;

      if (!result[workPlaceId]) {
        result[workPlaceId] = {
          workPlace: workPlace,
          workPlaceId: workPlaceId,
          devices: [],
        };
      }

      result[workPlaceId].devices.push({
        systemTypeId,
        deviceTypeId,
        piece,
        isAnotherProvider,
      });
    });

    return Object.values(result);
  };

  const handleSaveContract = async () => {
    try {
      modal.confirm({
        title: 'Confirm Update',
        content: 'Sigur doriți să actualizați punctele de lucru cu configurația curentă?',
        okText: 'Da',
        cancelText: 'Nu',
        onOk: async () => {
          const workPlaceItems = filterAndGroupData(addedDevices);
          workPlaceItems.forEach((workPlaceItem: any) => {
            const formData = {
              ...workPlaceItem?.workPlace,
              agents:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem.workPlace.id)?.agents ?? [],
              contactPersons:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem?.workPlace?.id)?.contactPersons ?? [],
              workPlaceItems: workPlaceItem.devices.map((d: any) => {
                return {
                  id: '',
                  workPlaceId: workPlaceItem.workPlaceId,
                  deviceTypeId: d.deviceTypeId,
                  systemTypeId: d.systemTypeId,
                  piece: d.piece,
                  metadata: '{}',
                  isAnotherProvider: d.isAnotherProvider ?? false,
                };
              }),
            };
            updateWorkPace(formData);
          });
          await onSaveContract();
        },
        onCancel: async () => {
          await onSaveContract();
        },
      });
    } catch (errorInfo: any) {
      message.error(errorInfo?.errorFields[0]?.errors);
    }
  };

  const handleResponse = (data: any) => {
    if (data?.errors?.length > 0) {
      data?.errors?.forEach((item: any) => {
        message.error(item);
      });
    } else {
      message.success(
        <span>
          <span className='font-weight-bold'>{data?.response?.number}</span> a fost {!!id ? 'modificat' : 'adaugat'} cu
          success
        </span>
      );
      navigate(`/project-management/pmContractProject/edit?id=${data?.response?.id}`);
    }
  };

  const { mutate: viewPdfPmContract, isPending: isViewingPdfPmContract } = useMutation({
    mutationFn: (formData: any) => PmContractService.preview(id, formData),
    onSuccess: (data: any) => {
      if (!data || !data.blob) {
        throw new Error('Invalid response received from the server');
      }
      return data.blob().then((blob: any) => {
        const url = window.URL.createObjectURL(blob);
        dispatch(
          updateEquipmentTab({
            field: 'pdfUrl',
            value: url,
          })
        );
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const viewPdf = () => {
    if (isShowCompanyDetailsTab) {
      viewPdfPmContract(reduxPmContractProject);
    }
  };

  const { mutate: printPmContractToPdf, isPending: isPrintingPmContract } = useMutation({
    mutationFn: () =>
      PmContractService.print({
        ids: [id!],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }),
    onSuccess: (response: any) => {
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);

        a.click();

        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const { mutate: generatePVRL, isPending: isGeneratingPVRLT } = useMutation({
    mutationFn: () =>
      PmContractService.generatePVRLT({
        ids: [id!],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
      }),
    onSuccess: (response: any) => {
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);

        a.click();

        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const tabs: (Tab | false)[] = [
    {
      key: 'companyTab',
      label: 'Detalii Client',
      children: <CompanyDetailsTab form={form} />,
    },
    isShowCompanyDetailsTab && {
      key: 'deviceTab',
      label: 'Adaugare Echipament',
      children: <AddEquipmentTab />,
    },
    addedDevices?.length > 0 && {
      key: 'summaryTab',
      label: (
        <>
          Vizualizare Echipamente Adaugate
          <Badge
            color='blue'
            style={{ marginLeft: '5px' }}
            count={addedDevices.length > 0 ? addedDevices.length : ''}
          ></Badge>
        </>
      ),
      children: <SummaryTab />,
    },
    addedDevices?.length > 0 && {
      key: 'manoperaTab',
      label: 'Manopera',
      children: <LaborTab />,
    },
    // addedDevices?.length! > 0 && {
    //   key: 'commentsTab',
    //   label: 'Observatii si Comentarii',
    //   children: <CommentsTab />,
    // },
    addedDevices?.length! > 0 &&
      isShowCompanyDetailsTab && {
        key: 'reviewTab',
        label: 'Review',
        children: <ReviewTab isViewingPdfPmContract={isViewingPdfPmContract} />, // Use key to force re-render
      },
    addedDevices?.length! > 0 &&
      isShowCompanyDetailsTab && {
        key: 'raportTab',
        label: 'Raport',
        children: <RaportTab />, // Use key to force re-render
      },
  ];

  const filteredTabs: Tab[] = tabs.filter((tab): tab is Tab => tab !== false);

  if (
    isPmContractLoading ||
    isCompaniesLoading ||
    isLaborExtrasLoading ||
    isStatusLoading ||
    isPmOfferTypesLoading ||
    isUnitMeasuresLoading ||
    isSystemTypesLoading ||
    isDeviceTypesLoading ||
    isPrintingPmContract ||
    isSavingContract ||
    isPmContractCommentsLoading ||
    isGeneratingPVRLT ||
    isUsersLoading ||
    isPmOffersLoading
  ) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <>
      <div className='topLine-pm'>
        <h2>{id ? 'Contract Proiect - ' + reduxPmContractProject.number : 'Contract Nou Proiect'}</h2>
        <div className='totalLine-pm'>
          <h3>{'Total - ' + totalOfferValue + ' lei'}</h3>
          {addedDevices?.some((item: any) => item.addition < 50)! && (
            <h2 style={{ color: 'red' }}>Adaosul produselor s-a modificat sub 50%</h2>
          )}
          <div className='buttons-pm'>
            <Button
              onClick={() => handleSaveContract()}
              className='saveBtn-pm'
              disabled={
                profile?.userPermission?.rols?.some((role: any) => role == 'senior_sales') ? false : canSavePmContract
              }
            >
              {isMobile ? (
                <Tooltip title='Salveaza Contract'>
                  <SaveOutlined />
                </Tooltip>
              ) : (
                'Salveaza Contract'
              )}
            </Button>

            {id!! && addedDevices?.length > 0 && (
              <Button
                className='download-pdf-pm'
                onClick={() => printPmContractToPdf()}
                disabled={
                  profile?.userPermission?.rols?.some((role: any) => role == 'senior_sales') ? false : canSavePmContract
                }
              >
                {isMobile ? (
                  <Tooltip title='Descărcare PDF'>
                    <DownloadOutlined />
                  </Tooltip>
                ) : (
                  <>
                    {' '}
                    <DownloadOutlined /> Descărcare PDF{' '}
                  </>
                )}
              </Button>
            )}

            {canGeneratePVRLT && (
              <Button className='download-pvrlt-pm' onClick={() => generatePVRL()}>
                {isMobile ? (
                  <Tooltip title='Genereaza PVRLT'>
                    <DownloadOutlined /> PVRLT
                  </Tooltip>
                ) : (
                  <>
                    {' '}
                    <DownloadOutlined /> Genereaza PVRLT{' '}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      <Tabs
        className='tabs'
        defaultActiveKey='1'
        items={filteredTabs}
        tabBarStyle={{ backgroundColor: '#DADBDD' }}
        tabBarGutter={18}
        onChange={(key) => {
          if (key === 'reviewTab') {
            viewPdf(); // Increment the key to force re-render
          }
        }}
      />
    </>
  );
};

export default NewPmContractProject;
